import { action, makeObservable, observable } from 'mobx';
import FilterStore from '../../Builder/ExperienceCatalog/FilterTemplate/Store';
import SortStore from '../../Builder/ExperienceCatalog/SortTemplate/Store';
import { trendingFirst } from '../../Builder/ExperienceCatalog/SortTemplate/Types';
import { FilterOptionData } from '../../Builder/ExperienceCatalog/Types';
import UnlicensedToggleStore from './UnlicensedToggleStore';

/**
 * PersistentFilterStore is a singleton that maintains filter state across
 * component unmounts/remounts. This prevents filter resets when navigating
 * between the catalog and other views.
 *
 * The store caches:
 * - Filter selections for all filter types
 * - Sort selection
 * - Unlicensed toggle state
 *
 * When the catalog is reopened, it reuses the existing stores if they
 * are still valid (i.e., the filter options haven't changed).
 */
class PersistentFilterStore {
  @observable
  private _productTypeFilterStore: FilterStore | null = null;

  @observable
  private _durationFilterStore: FilterStore | null = null;

  @observable
  private _schoolPartnerFilterStore: FilterStore | null = null;

  @observable
  private _languageFilterStore: FilterStore | null = null;

  @observable
  private _licenseFilterStore: FilterStore | null = null;

  @observable
  private _competencyFilterStore: FilterStore | null = null;

  @observable
  private _sortStore: SortStore | null = null;

  @observable
  private _unlicensedToggleStore: UnlicensedToggleStore | null = null;

  // Store a hash of the options to detect when we need to recreate stores
  private _optionsHash: string = '';

  constructor() {
    makeObservable(this);
  }

  /**
   * Creates a simple hash of filter options for comparison
   */
  private hashOptions(
    productTypeOptions: FilterOptionData[],
    schoolPartnerOptions: FilterOptionData[],
    languageOptions: FilterOptionData[],
    durationOptions: FilterOptionData[],
    licenseOptions: FilterOptionData[],
    competencyOptions: FilterOptionData[],
  ): string {
    const hash = [
      productTypeOptions.map((o) => o.value).join(','),
      schoolPartnerOptions.map((o) => o.value).join(','),
      languageOptions.map((o) => o.value).join(','),
      durationOptions.map((o) => o.value).join(','),
      licenseOptions.map((o) => o.value).join(','),
      competencyOptions.map((o) => o.value).join(','),
    ].join('|');
    return hash;
  }

  /**
   * Gets or creates filter stores. If the options match the cached options,
   * returns the existing stores. Otherwise, creates new stores.
   */
  @action
  getOrCreateStores(
    productTypeOptions: FilterOptionData[],
    schoolPartnerOptions: FilterOptionData[],
    languageOptions: FilterOptionData[],
    durationOptions: FilterOptionData[],
    licenseOptions: FilterOptionData[],
    competencyOptions: FilterOptionData[],
  ): {
    productTypeFilterStore: FilterStore;
    durationFilterStore: FilterStore;
    schoolPartnerFilterStore: FilterStore;
    languageFilterStore: FilterStore;
    licenseFilterStore: FilterStore;
    competencyFilterStore: FilterStore;
    sortStore: SortStore;
    unlicensedToggleStore: UnlicensedToggleStore;
  } {
    const newHash = this.hashOptions(
      productTypeOptions,
      schoolPartnerOptions,
      languageOptions,
      durationOptions,
      licenseOptions,
      competencyOptions,
    );

    // If options changed or stores don't exist, create new stores
    if (
      newHash !== this._optionsHash ||
      !this._productTypeFilterStore ||
      !this._durationFilterStore ||
      !this._schoolPartnerFilterStore ||
      !this._languageFilterStore ||
      !this._licenseFilterStore ||
      !this._competencyFilterStore ||
      !this._sortStore ||
      !this._unlicensedToggleStore
    ) {
      this._optionsHash = newHash;
      this._productTypeFilterStore = new FilterStore(productTypeOptions);
      this._schoolPartnerFilterStore = new FilterStore(schoolPartnerOptions);
      this._languageFilterStore = new FilterStore(languageOptions);
      this._durationFilterStore = new FilterStore(durationOptions);
      this._licenseFilterStore = new FilterStore(licenseOptions);
      this._competencyFilterStore = new FilterStore(competencyOptions);
      this._sortStore = new SortStore(trendingFirst);
      this._unlicensedToggleStore = new UnlicensedToggleStore();
    }

    // All stores are guaranteed to be initialized after the check above
    return {
      productTypeFilterStore: this._productTypeFilterStore,
      durationFilterStore: this._durationFilterStore,
      schoolPartnerFilterStore: this._schoolPartnerFilterStore,
      languageFilterStore: this._languageFilterStore,
      licenseFilterStore: this._licenseFilterStore,
      competencyFilterStore: this._competencyFilterStore,
      sortStore: this._sortStore,
      unlicensedToggleStore: this._unlicensedToggleStore,
    };
  }

  /**
   * Clears all cached stores. Call this when the builder is unmounted
   * or when you want to reset all filters.
   */
  @action
  clear(): void {
    this._productTypeFilterStore = null;
    this._durationFilterStore = null;
    this._schoolPartnerFilterStore = null;
    this._languageFilterStore = null;
    this._licenseFilterStore = null;
    this._competencyFilterStore = null;
    this._sortStore = null;
    this._unlicensedToggleStore = null;
    this._optionsHash = '';
  }
}

// Export a singleton instance
export const persistentFilterStore = new PersistentFilterStore();
